import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit,
  increment
} from '@angular/fire/firestore';
import { from, map, switchMap, tap } from 'rxjs';
import { Image } from '../models/image';
import { Video } from '../models/video';

@Injectable({ providedIn: 'root' })
export class MediaService {
  private firestore = inject(Firestore);
  private imagesCol = collection(this.firestore, 'images');
  private videosCol = collection(this.firestore, 'videos');

  uploadImage(data: { userId: string; firebaseUrl: string; description?: string }) {
    const payload = {
      userId: data.userId,
      firebaseUrl: data.firebaseUrl,
      description: data.description || '',
      created_at: serverTimestamp(),
      likesCount: 0,
      viewsCount: 0
    };
    return from(addDoc(this.imagesCol, payload)).pipe(
      tap(() => {
        const modelRef = doc(this.firestore, 'models', data.userId);
        updateDoc(modelRef, { imagesCount: increment(1) }).catch(() => {});
      }),
      map(ref => Image.fromFirestore(ref.id, { ...payload, created_at: new Date() }))
    );
  }

  uploadVideo(data: { userId: string; firebaseUrl: string; description?: string }) {
    const payload = {
      userId: data.userId,
      firebaseUrl: data.firebaseUrl,
      description: data.description || '',
      created_at: serverTimestamp(),
      likesCount: 0,
      viewsCount: 0
    };
    return from(addDoc(this.videosCol, payload)).pipe(
      tap(() => {
        const modelRef = doc(this.firestore, 'models', data.userId);
        updateDoc(modelRef, { videosCount: increment(1) }).catch(() => {});
      }),
      map(ref => Video.fromFirestore(ref.id, { ...payload, created_at: new Date() }))
    );
  }

  getImageById(id: string) {
    const ref = doc(this.firestore, 'images', id);
    return from(import('@angular/fire/firestore').then(m => m.getDoc(ref))).pipe(
      map(snap => {
        if (!snap.exists()) throw new Error('Image not found');
        return Image.fromFirestore(snap.id, snap.data());
      })
    );
  }

  getVideoById(id: string) {
    const ref = doc(this.firestore, 'videos', id);
    return from(import('@angular/fire/firestore').then(m => m.getDoc(ref))).pipe(
      map(snap => {
        if (!snap.exists()) throw new Error('Video not found');
        return Video.fromFirestore(snap.id, snap.data());
      })
    );
  }

  getUserImages(userId: string) {
    const q = query(this.imagesCol, where('userId', '==', userId));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(d => Image.fromFirestore(d.id, d.data())))
    );
  }

  getUserVideos(userId: string) {
    const q = query(this.videosCol, where('userId', '==', userId));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(d => Video.fromFirestore(d.id, d.data())))
    );
  }

  /**
   * Returns aggregated media counts for a given user/model.
   * Useful for keeping images/videos counts consistent across pages.
   */
  getUserMediaCounts(userId: string) {
    const imagesQ = query(this.imagesCol, where('userId', '==', userId));
    const videosQ = query(this.videosCol, where('userId', '==', userId));
    return from(Promise.all([getDocs(imagesQ), getDocs(videosQ)])).pipe(
      map(([imagesSnap, videosSnap]) => ({
        imagesCount: imagesSnap.size,
        videosCount: videosSnap.size
      }))
    );
  }

  deleteImage(id: string) {
    const imgRef = doc(this.firestore, 'images', id);
    return from(getDoc(imgRef)).pipe(
      switchMap(snap => {
        const userId = snap.data()?.['userId'];
        return from(deleteDoc(imgRef)).pipe(
          tap(() => {
            if (userId) {
              const modelRef = doc(this.firestore, 'models', userId);
              updateDoc(modelRef, { imagesCount: increment(-1) }).catch(() => {});
            }
          }),
          map(() => id)
        );
      })
    );
  }

  deleteVideo(id: string) {
    const vidRef = doc(this.firestore, 'videos', id);
    return from(getDoc(vidRef)).pipe(
      switchMap(snap => {
        const userId = snap.data()?.['userId'];
        return from(deleteDoc(vidRef)).pipe(
          tap(() => {
            if (userId) {
              const modelRef = doc(this.firestore, 'models', userId);
              updateDoc(modelRef, { videosCount: increment(-1) }).catch(() => {});
            }
          }),
          map(() => id)
        );
      })
    );
  }

  /** Get latest images ordered by created_at descending */
  getLatestImages(count: number = 8) {
    const q = query(this.imagesCol, orderBy('created_at', 'desc'), limit(count));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(d => Image.fromFirestore(d.id, d.data())))
    );
  }

  /** Get latest videos ordered by created_at descending */
  getLatestVideos(count: number = 8) {
    const q = query(this.videosCol, orderBy('created_at', 'desc'), limit(count));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(d => Video.fromFirestore(d.id, d.data())))
    );
  }
}
